import { Engine } from '../engine/Engine';
import { MagicBallScene } from '../scenes/magic-ball/MagicBallScene';
import { postProcess, faceMappings, accelerometer as accelConfig, environmentPresets } from '../scenes/magic-ball/MagicBallConfig';
import { DebugMenu } from '../scenes/magic-ball/DebugMenu';
import { MessageTextureGenerator } from '../scenes/magic-ball/MessageTextureGenerator';
import type {
  AccelerometerData,
  CameraOptions,
  EnvironmentOptions,
  BallAppearance,
  MagicBallState,
  FaceInfo,
} from './types';

declare global {
  interface Window {
    MagicBall?: MagicBallAPI;
    webkit?: {
      messageHandlers?: {
        magicBall?: { postMessage: (msg: unknown) => void };
      };
    };
  }
}

export class MagicBallAPI {
  private engine: Engine;
  private scene: MagicBallScene;
  private debugMenu: DebugMenu | null = null;
  private isIdle = true;
  private currentLanguage = 'en';
  private currentMessages: string[] = faceMappings.map((f) => f.message);
  private languageManifest: { code: string; name: string; nativeName: string; rtl: boolean }[] | null = null;

  // Shake-hide bookkeeping. `shakeHideActive` is set the instant a shake
  // gesture begins (button press or first accelerometer spike) and stays
  // true until `endShakeHide` flips it back. While active, `icoMesh.visible`
  // is forced false, so the old face is gone for the entire shake — no
  // matter how long the user keeps shaking. `shakeRevealTimer` is the
  // pending reveal: every fresh spike bumps it further into the future so
  // the reveal only fires once the shake actually settles.
  private shakeHideActive = false;
  private shakeRevealTimer: ReturnType<typeof setTimeout> | null = null;
  /// How long after the last shake spike (or after a button-press wobble
  /// starts) before the ico is allowed to rise back into view.
  private static readonly BUTTON_REVEAL_DELAY_MS = 600;
  private static readonly DEVICE_REVEAL_DELAY_MS = 500;

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas);
    this.engine.setPostProcessConfig(postProcess);
    this.scene = new MagicBallScene();
  }

  async init(): Promise<void> {
    await this.engine.loadScene(this.scene);

    // Start in idle mode: icosahedron hidden, red "?" sprite shown.
    // Native shell triggers `firstShake` to flip out of this state.
    this.scene.setIdleMode(true);

    const controller = this.scene.getController();
    if (controller) {
      controller.onFaceChange = (index: number) => {
        const msg = this.currentMessages[index] ?? '';
        this.emit('faceChanged', { index, message: msg });
        this.emit('animationEnd', { face: index, message: msg });
      };
    }

    const interaction = this.scene.getInteraction();
    if (interaction) {
      const origShake = interaction.onShakeTriggered;
      interaction.onShakeTriggered = () => {
        // Drag-shake (finger drag past threshold) and the wobble's own
        // completion both flow through this callback. If a shake-hide is
        // already in progress (button or device shake), our scheduled
        // reveal handles the face change — skip the auto-`showRandom` so
        // we don't race the timer.
        if (this.shakeHideActive) return;
        if (this.isIdle) this.setIdleMode(false);
        this.emit('animationStart', { type: 'shake' });
        if (typeof origShake === 'function') origShake();
      };
    }

    this.engine.start();

    window.MagicBall = this;

    const host = location.hostname;
    const isDevHost =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      /^192\.168\./.test(host) ||
      /^10\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
      new URLSearchParams(location.search).has('debug');
    if (isDevHost) {
      this.debugMenu = new DebugMenu(this, this.scene);
    }

    this.emit('ready', {});
  }

  // --- Idle Mode ---

  /**
   * Toggles between idle mode (icosahedron hidden, red "?" sprite visible)
   * and answer mode (icosahedron visible, sprite hidden). The transition from
   * idle → answer emits `firstShake` so the native shell can swap its
   * overlays (welcome prompt → quoted-answer banner).
   */
  setIdleMode(idle: boolean): void {
    if (this.isIdle === idle) return;
    const wasIdle = this.isIdle;
    this.isIdle = idle;
    this.scene.setIdleMode(idle);
    if (wasIdle && !idle) {
      this.emit('firstShake', {});
    }
  }

  // --- Face Control ---

  randomize(): void {
    const controller = this.scene.getController();
    if (controller && !controller.isAnimating) {
      if (this.isIdle) this.setIdleMode(false);
      this.emit('animationStart', { type: 'reveal' });
      controller.showRandom();
    }
  }

  showFace(index: number): void {
    const controller = this.scene.getController();
    if (controller && !controller.isAnimating) {
      this.emit('animationStart', { type: 'reveal', targetFace: index });
      controller.showFace(index);
    }
  }

  showMessage(message: string): void {
    const index = faceMappings.findIndex(
      (f) => f.message.toLowerCase() === message.toLowerCase(),
    );
    if (index >= 0) this.showFace(index);
  }

  // --- Physical Interaction ---

  simulateShake(): void {
    const interaction = this.scene.getInteraction();
    if (!interaction) return;
    // Hard-hide the ico on the same frame the button fires, then run the
    // wobble over the empty window, then reveal the new face after the
    // wobble settles. No animation between press and disappearance.
    this.beginShakeHide();
    interaction.simulateShake();
    this.scheduleShakeReveal(MagicBallAPI.BUTTON_REVEAL_DELAY_MS);
  }

  updateAccelerometer(data: AccelerometerData): void {
    const interaction = this.scene.getInteraction();
    if (!interaction) return;

    const tiltX = Math.max(-1, Math.min(1, data.x / 9.8));
    const tiltY = Math.max(-1, Math.min(1, data.y / 9.8));
    interaction.applyDeviceTilt(tiltX, tiltY);

    const magnitude = Math.sqrt(data.x ** 2 + data.y ** 2 + data.z ** 2);
    const net = Math.abs(magnitude - 9.8);
    this.checkShake(net);
  }

  enableGestures(enabled: boolean): void {
    const interaction = this.scene.getInteraction();
    if (interaction) {
      interaction.setEnabled(enabled);
    }
  }

  // --- Camera ---

  setCamera(options: CameraOptions): void {
    const cam = this.scene.getCameraSystem();
    if (!cam) return;

    // OrbitControls flags always apply immediately even when the rest of
    // the call is animated.
    if (options.autoRotate !== undefined) cam.controls.autoRotate = options.autoRotate;
    if (options.autoRotateSpeed !== undefined) cam.controls.autoRotateSpeed = options.autoRotateSpeed;

    if (options.animate) {
      cam.animateTo(options.position, options.target, options.fov, options.duration ?? 500);
      return;
    }

    if (options.position) cam.camera.position.set(...options.position);
    if (options.target) cam.controls.target.set(...options.target);
    if (options.fov !== undefined) {
      cam.camera.fov = options.fov;
      cam.camera.updateProjectionMatrix();
    }
    cam.controls.update();
  }

  setAutoRotate(enabled: boolean): void {
    const cam = this.scene.getCameraSystem();
    if (cam) cam.controls.autoRotate = enabled;
  }

  /**
   * Snapshot of the live camera state, used by the native shell to compute
   * shake/parallax offsets relative to whatever zoom or orbit position the
   * user has dialed in via gestures.
   */
  getCamera(): { position: [number, number, number]; target: [number, number, number]; fov: number } | null {
    const cam = this.scene.getCameraSystem();
    if (!cam) return null;
    return {
      position: cam.camera.position.toArray() as [number, number, number],
      target: cam.controls.target.toArray() as [number, number, number],
      fov: cam.camera.fov,
    };
  }

  // --- Environment ---

  setEnvironment(options: EnvironmentOptions): void {
    const scene = this.scene.getScene();
    if (options.intensity !== undefined) {
      scene.environmentIntensity = options.intensity;
    }
    if (options.showAsBackground !== undefined) {
      scene.background = options.showAsBackground ? scene.environment : null;
    }
  }

  async loadEnvironment(presetOrPath: string, options?: { intensity?: number; asBackground?: boolean }): Promise<void> {
    const isPreset = presetOrPath in environmentPresets;
    const url = isPreset ? environmentPresets[presetOrPath] : presetOrPath;
    const asBackground = options?.asBackground ?? true;
    await this.scene.swapEnvironment(url, options?.intensity, asBackground, isPreset ? presetOrPath : undefined);
  }

  getEnvironmentPresets(): string[] {
    return Object.keys(environmentPresets);
  }

  // --- Appearance ---

  setAppearance(options: BallAppearance): void {
    this.scene.updateAppearance(options);
  }

  setBallColor(color: string | number): void {
    this.scene.updateBallColor(color);
  }

  async setBallTexture(bundlePath: string): Promise<void> {
    await this.scene.updateBallTexture(bundlePath);
  }

  setEmissiveColor(color: string | number): void {
    this.scene.updateEmissiveColor(color);
  }

  setWindowTint(color: string | number): void {
    this.scene.updateWindowTint(color);
  }

  setBackgroundColor(color: string | number | null): void {
    this.scene.updateBackground(color);
  }

  // --- Localization ---

  async setLanguage(code: string): Promise<void> {
    if (code === 'en') {
      this.scene.restoreOriginalTextures();
      this.currentMessages = faceMappings.map((f) => f.message);
      this.currentLanguage = 'en';
      this.emit('languageChanged', { code: 'en' });
      return;
    }

    const res = await fetch(`locales/${code}.json`);
    if (!res.ok) {
      this.emit('error', { message: `Locale not found: ${code}` });
      return;
    }
    const locale = await res.json() as { code: string; messages: string[] };
    this.applyMessages(locale.messages);
    this.currentLanguage = code;
    this.emit('languageChanged', { code });
  }

  setCustomMessages(messages: string[]): void {
    const padded = Array.from({ length: 20 }, (_, i) => messages[i] ?? '');
    this.applyMessages(padded);
    this.currentLanguage = 'custom';
    this.emit('languageChanged', { code: 'custom' });
  }

  async getLanguages(): Promise<{ code: string; name: string; nativeName: string; rtl: boolean }[]> {
    if (this.languageManifest) return this.languageManifest;
    const res = await fetch('locales/index.json');
    this.languageManifest = await res.json();
    return this.languageManifest!;
  }

  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  private applyMessages(messages: string[]): void {
    const sourceImage = this.scene.getIcoSourceImage();
    if (!sourceImage) {
      this.emit('error', { message: 'Source texture not available' });
      return;
    }
    const generator = new MessageTextureGenerator(sourceImage);
    const texture = generator.generate(messages);
    this.scene.applyMessageTextures(texture);
    this.currentMessages = [...messages];
  }

  // --- State ---

  getState(): MagicBallState {
    const controller = this.scene.getController();
    const face = controller?.currentFace ?? 0;
    return {
      currentFace: face,
      message: this.currentMessages[face] ?? '',
      isAnimating: controller?.isAnimating ?? false,
      faceCount: faceMappings.length,
    };
  }

  getFaces(): FaceInfo[] {
    return this.currentMessages.map((m, i) => ({ index: i, message: m }));
  }

  // --- Lifecycle ---

  dispose(): void {
    this.debugMenu?.dispose();
    this.engine.dispose();
    if (window.MagicBall === this) {
      window.MagicBall = undefined;
    }
  }

  // --- Internal ---

  private emit(event: string, data: Record<string, unknown> = {}): void {
    const payload = { event, data };

    if (window.webkit?.messageHandlers?.magicBall) {
      window.webkit.messageHandlers.magicBall.postMessage(payload);
    }

    window.dispatchEvent(new CustomEvent('magicball', { detail: payload }));
  }

  private checkShake(netAccel: number): void {
    if (netAccel <= accelConfig.shakeThreshold) return;

    // Hide the ico on the very first spike of a fresh gesture (or any
    // spike that arrives after a previous reveal completed). Every
    // subsequent spike just bumps the reveal timer further out — so the
    // ico stays gone for the full duration of the shake, no matter how
    // long it lasts, and only reappears DEVICE_REVEAL_DELAY_MS after the
    // user actually stops moving the phone.
    if (!this.shakeHideActive) {
      this.beginShakeHide();
    }
    this.scheduleShakeReveal(MagicBallAPI.DEVICE_REVEAL_DELAY_MS);
  }

  // --- Shake hide / reveal ---

  /// Hard-hides the icosahedron and emits the start-of-shake event. Safe
  /// to call repeatedly — only the first call has any effect until the
  /// matching `endShakeHide` runs.
  private beginShakeHide(): void {
    if (this.shakeHideActive) return;
    this.shakeHideActive = true;
    if (this.isIdle) this.setIdleMode(false);
    this.scene.setIcoVisibility(false);
    this.emit('animationStart', { type: 'shake' });
  }

  /// (Re)schedules the reveal `delayMs` from now. Each call clears any
  /// pending reveal — so a stream of accelerometer spikes keeps pushing
  /// the reveal forward until the shake genuinely stops.
  private scheduleShakeReveal(delayMs: number): void {
    if (this.shakeRevealTimer !== null) {
      clearTimeout(this.shakeRevealTimer);
    }
    this.shakeRevealTimer = setTimeout(() => {
      this.endShakeHide();
    }, delayMs);
  }

  /// Reveals the ico with a new random face. Re-enables visibility first
  /// so the rise animation has something to draw, then runs the controller
  /// with `skipSink=true` (we don't need to sink — we're already hidden).
  private endShakeHide(): void {
    if (!this.shakeHideActive) return;
    this.shakeHideActive = false;
    this.shakeRevealTimer = null;
    this.scene.setIcoVisibility(true);
    const controller = this.scene.getController();
    if (controller && !controller.isAnimating) {
      controller.showRandom(true);
    }
  }
}
